import type { TKey } from "@/lib/i18n";

/**
 * The one period of the whole app — what the Top bar's period button shows
 * and every screen under it reads (доска Top, ReportCompare). It lives in the
 * URL, so a link to «кампании за прошлый месяц против того же месяца год назад
 * по дате конверсии» opens exactly that:
 *
 *   ?preset=7d                      — or preset=custom&from=…&to=…
 *   ?cmp=previous | year | off      — what the deltas compare against (default previous;
 *                                     an old link's cmp=prev still reads as previous)
 *   ?cmp=custom&cmp_from=…&cmp_to=… — against a range of the person's own
 *   ?by=lead | conv | click         — a lead counts on the day it arrived (default), its status's, or its click's
 *   ?who=mine | all                 — only my campaigns, or everybody's (the workspace
 *                                     is shared, there are no teams). Default: a buyer
 *                                     (tracker-member, «своё») opens on «Мои», the rest
 *                                     on «Все» (`defaultScope`). «Мои» is the server's
 *                                     identity of the caller (`mine` on the request) —
 *                                     the client never names itself.
 *
 * Days are the workspace's (решение 03): `Workspace.timezone`, Europe/Kyiv by
 * default. Requests carry no zone — the server counts in the workspace's — and
 * the presets here are drawn in the same zone (`useReportTimezone`), so
 * «сегодня» on the screen is the server's today.
 */

export type DatePreset = "today" | "yesterday" | "7d" | "14d" | "30d" | "mtd" | "lastmonth" | "custom";
export type FixedPreset = Exclude<DatePreset, "custom">;
/** The package PeriodPicker's words (@fox1ck-org/ui/date `PeriodCompare`). */
export type CompareMode = "previous" | "year" | "custom" | "off";
export type ConversionBasis = "lead" | "conv" | "click";
export type Scope = "mine" | "all";

export const DATE_PRESETS: readonly DatePreset[] = [
  "today",
  "yesterday",
  "7d",
  "14d",
  "30d",
  "mtd",
  "lastmonth",
  "custom",
];
/** A preset's words, the same on the Top bar and the phone. */
export const PRESET_LABEL: Record<DatePreset, TKey> = {
  today: "period.today",
  yesterday: "period.yesterday",
  "7d": "period.7d",
  "14d": "period.14d",
  "30d": "period.30d",
  mtd: "period.mtd",
  lastmonth: "period.lastmonth",
  custom: "period.custom",
};

export const COMPARE_MODES: readonly CompareMode[] = ["previous", "year", "custom", "off"];
export const CONVERSION_BASES: readonly ConversionBasis[] = ["lead", "conv", "click"];
export const SCOPES: readonly Scope[] = ["mine", "all"];

/** The workspace zone until the workspace is loaded — the server's default too. */
export const DEFAULT_REPORT_TZ = "Europe/Kyiv";

export interface DayRange {
  from: string;
  to: string;
}

const ISO = /^\d{4}-\d{2}-\d{2}$/;

export function isDatePreset(v: string | null | undefined): v is DatePreset {
  return !!v && (DATE_PRESETS as readonly string[]).includes(v);
}

function pick<T extends string>(all: readonly T[], v: string | null | undefined, fallback: T): T {
  return v && (all as readonly string[]).includes(v) ? (v as T) : fallback;
}

// `prev` is how links said «previous» before the package's PeriodPicker.
export const parseCompare = (v: string | null | undefined): CompareMode => pick(COMPARE_MODES, v === "prev" ? "previous" : v, "previous");
/**
 * Decision 02: by default a lead lives on the day it arrived (first_ts);
 * «по дате конверсии» (its status's time) and «по дате клика» are the switch.
 */
export const parseBasis = (v: string | null | undefined): ConversionBasis => pick(CONVERSION_BASES, v, "lead");
/** `fallback` is the caller's default (`defaultScope`) when the address says nothing. */
export const parseScope = (v: string | null | undefined, fallback: Scope = "all"): Scope => pick(SCOPES, v, fallback);

function utcDay(y: number, m: number, d: number): Date {
  return new Date(Date.UTC(y, m, d));
}

export function isoDay(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** ISO day ± n days. Pure. */
export function addDays(iso: string, n: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  return isoDay(utcDay(y!, m! - 1, d! + n));
}

/** Inclusive length of a range in days. Pure. */
export function daysIn(r: DayRange): number {
  const ms = Date.parse(`${r.to}T00:00:00Z`) - Date.parse(`${r.from}T00:00:00Z`);
  return Number.isFinite(ms) && ms >= 0 ? Math.round(ms / 86_400_000) + 1 : 1;
}

/** The calendar day `now` falls on in `tz`, as ISO. Pure. */
export function dayIn(now: Date, tz: string): string {
  // en-CA formats as YYYY-MM-DD.
  return new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
}

/**
 * A preset as an inclusive day range in `tz`, anchored on `now`. Trailing
 * windows include today (7 дней = today and the six before). Pure.
 */
export function rangeForPreset(preset: FixedPreset, now: Date = new Date(), tz = "UTC"): DayRange {
  const today = dayIn(now, tz);
  const [ty, tm] = today.split("-").map(Number);
  const y = ty!;
  const m = tm! - 1;
  switch (preset) {
    case "today":
      return { from: today, to: today };
    case "yesterday": {
      const d = addDays(today, -1);
      return { from: d, to: d };
    }
    case "7d":
      return { from: addDays(today, -6), to: today };
    case "14d":
      return { from: addDays(today, -13), to: today };
    case "30d":
      return { from: addDays(today, -29), to: today };
    case "mtd":
      return { from: isoDay(utcDay(y, m, 1)), to: today };
    case "lastmonth":
      return { from: isoDay(utcDay(y, m - 1, 1)), to: isoDay(utcDay(y, m, 0)) };
  }
}

export interface ResolvedRange extends DayRange {
  preset: DatePreset;
}

/**
 * Raw URL values → the effective preset and bounds. An invalid or partial
 * custom range falls back to `fallback`, so a hand-edited link degrades to a
 * default view rather than to a blank screen. Pure.
 */
export function resolveRange(
  raw: { preset?: string | null; from?: string | null; to?: string | null },
  now: Date = new Date(),
  fallback: FixedPreset = "7d",
  tz = "UTC",
): ResolvedRange {
  const preset = isDatePreset(raw.preset) ? raw.preset : fallback;
  if (preset === "custom") {
    const from = raw.from ?? "";
    const to = raw.to ?? "";
    if (ISO.test(from) && ISO.test(to) && from <= to) return { preset, from, to };
    return { preset: fallback, ...rangeForPreset(fallback, now, tz) };
  }
  return { preset, ...rangeForPreset(preset, now, tz) };
}

/**
 * What the period is compared against. «Прошлый период» is the same number of
 * days ending the day before; «год назад» is the same calendar days a year
 * earlier (29 Feb falls back to 28); `custom` is the person's own range. `off`
 * has no comparison. Pure.
 */
export function compareRange(r: DayRange, mode: CompareMode, custom?: Partial<DayRange>): DayRange | undefined {
  if (mode === "off") return undefined;
  if (mode === "custom") {
    // A hand-picked comparison without both days compares with the previous period.
    if (custom?.from && custom.to && ISO.test(custom.from) && ISO.test(custom.to) && custom.from <= custom.to) return { from: custom.from, to: custom.to };
    return compareRange(r, "previous");
  }
  if (mode === "previous") {
    const n = daysIn(r);
    return { from: addDays(r.from, -n), to: addDays(r.from, -1) };
  }
  const back = (iso: string) => {
    const [y, m, d] = iso.split("-").map(Number);
    const target = utcDay(y! - 1, m! - 1, d!);
    // Date.UTC rolls 29.02 into 01.03; a year back of the 29th is the 28th.
    return target.getUTCMonth() === m! - 1 ? isoDay(target) : isoDay(utcDay(y! - 1, m!, 0));
  };
  return { from: back(r.from), to: back(r.to) };
}
